import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { toast } from '../components/Toast';
import * as XLSX from 'xlsx';

const StudentReport = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busCodes, setBusCodes] = useState([]);
    const [selectedBusCode, setSelectedBusCode] = useState('');
    const [showAll, setShowAll] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        loadStudents();
    }, []);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedBusCode, showAll]);

    const loadStudents = async () => {
        setLoading(true);
        try {
            // Fetch students
            const studentsRes = await apiFetch('/students');
            const studentsData = studentsRes.data || [];
            setStudents(studentsData);
            
            // Fetch all vehicles to get bus codes
            const vehiclesRes = await apiFetch('/vehicles');
            const vehiclesData = vehiclesRes.data || [];
            
            // Extract unique vehicle codes from vehicles and sort them
            const codes = [...new Set(vehiclesData.map(v => v.vehicle_code).filter(Boolean))].sort();
            setBusCodes(codes);
        } catch (e) {
            toast.error(e.message || "Failed loading students");
        } finally {
            setLoading(false);
        }
    };

    // Filter and sort students
    const getFilteredAndSortedStudents = () => {
        let filtered = showAll 
            ? [...students] 
            : students.filter(s => s.vehicle_code === selectedBusCode);
        
        // Sort by bus code, then by name
        filtered.sort((a, b) => {
            const busCodeA = a.vehicle_code || '';
            const busCodeB = b.vehicle_code || '';
            
            if (busCodeA !== busCodeB) {
                return busCodeA.localeCompare(busCodeB);
            }
            
            return (a.full_name || '').localeCompare(b.full_name || '');
        });
        
        return filtered;
    };

    const filteredStudents = getFilteredAndSortedStudents();
    
    // Pagination
    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

    const handleShowAllChange = (e) => {
        setShowAll(e.target.checked);
    };

    const handleBusCodeChange = (e) => {
        setSelectedBusCode(e.target.value);
    };

    const downloadExcel = () => {
        if (filteredStudents.length === 0) return toast.error("No student data to export");
        
        // Group students by bus code
        const groupedByBus = {};
        filteredStudents.forEach(s => {
            const busCode = s.vehicle_code || 'Unassigned';
            if (!groupedByBus[busCode]) {
                groupedByBus[busCode] = [];
            }
            groupedByBus[busCode].push(s);
        });
        
        // Create data with bus code headers
        let data = [];
        const busCodes = Object.keys(groupedByBus).sort();
        
        busCodes.forEach(busCode => {
            // Add bus code as a header row
            data.push({
                'Bus Code': busCode,
                'Student Name': '',
                'Registration Number': '',
                'Grade': '',
                'Route': '',
                'Stop': '',
                'Parent Name': '',
                'Phone': ''
            });
            
            // Add students under this bus code
            groupedByBus[busCode].forEach(s => {
                data.push({
                    'Bus Code': '',
                    'Student Name': s.full_name,
                    'Registration Number': s.registration_number || '-',
                    'Grade': s.grade || '-',
                    'Route': s.route_code || s.route_name || '-',
                    'Stop': s.stop_name || s.stop?.stop_name || '-',
                    'Parent Name': s.parent_name || s.parent?.full_name || '-',
                    'Phone': s.parent_phone || s.parent?.phone || '-'
                });
            });
            
            // Add empty row between bus codes
            data.push({
                'Bus Code': '',
                'Student Name': '',
                'Registration Number': '',
                'Grade': '',
                'Route': '',
                'Stop': '',
                'Parent Name': '',
                'Phone': ''
            });
        });
        
        // Add summary row
        data.push({
            'Bus Code': 'TOTAL',
            'Student Name': '',
            'Registration Number': '',
            'Grade': '',
            'Route': '',
            'Stop': '',
            'Parent Name': `Total Buses: ${busCodes.length}`,
            'Phone': `Total Students: ${filteredStudents.length}`
        });
        
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Students");
        const date = new Date().toISOString().split('T')[0];
        const busCodeSuffix = showAll ? 'all' : selectedBusCode;
        XLSX.writeFile(wb, `students_report_${busCodeSuffix}_${date}.xlsx`);
    };

    return (
        <div>
            <h1 className="page-title text-gradient" style={{ margin: 0, marginBottom: '24px' }}>Student Report</h1>

            <div style={{ 
                display: 'flex', 
                gap: '10px', 
                alignItems: 'center', 
                marginBottom: '16px',
                flexWrap: 'wrap'
            }}>
                {/* All Checkbox */}
                <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    height: '40px'
                }}>
                    <input
                        type="checkbox"
                        checked={showAll}
                        onChange={handleShowAllChange}
                    />
                    <span>All</span>
                </label>

                {/* Bus Code Dropdown */}
                <select
                    value={selectedBusCode}
                    onChange={handleBusCodeChange}
                    disabled={showAll}
                    style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '6px', 
                        width: '200px', 
                        height: '40px',
                        backgroundColor: showAll ? '#f5f5f5' : '#fff',
                        cursor: showAll ? 'not-allowed' : 'pointer'
                    }}
                >
                    <option value="">Select Bus Code</option>
                    {busCodes.map(code => (
                        <option key={code} value={code}>{code}</option>
                    ))}
                </select>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                    <button
                        onClick={downloadExcel}
                        style={{
                            padding: '6px 16px',
                            background: '#000',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            height: '40px'
                        }}
                    >
                        Download Excel
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div style={{ marginBottom: '16px', color: 'var(--text-muted)' }}>
                Showing {filteredStudents.length} students 
                {showAll ? '(All Bus Codes)' : `(Bus Code: ${selectedBusCode || 'None'})`}
            </div>

            <div className="glass-panel" style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ fontSize: '14px', width: '100%' }}>
                    <thead>
                        <tr style={{ height: '48px' }}>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Student Name</th>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Registration Number</th>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Grade</th>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Route</th>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Stop</th>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Bus Code</th>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Parent Name</th>
                            <th style={{ padding: '10px 16px', textAlign: 'center' }}>Phone</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr style={{ height: '48px' }}>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '10px 16px' }}>Loading students...</td>
                            </tr>
                        ) : paginatedStudents.length === 0 ? (
                            <tr style={{ height: '48px' }}>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '10px 16px' }}>
                                    {showAll ? 'No students found.' : 'No students found for selected bus code.'}
                                </td>
                            </tr>
                        ) : (
                            paginatedStudents.map((s) => (
                                <tr key={s.student_id} style={{ height: '48px' }}>
                                    <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600' }}>{s.full_name}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{s.registration_number || '-'}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{s.grade || '-'}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{s.route_code || s.route_name || '-'}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{s.stop_name || s.stop?.stop_name || '-'}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{s.vehicle_code || '-'}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{s.parent_name || s.parent?.full_name || '-'}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>{s.parent_phone || s.parent?.phone || '-'}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '16px'
                }}>
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        style={{
                            padding: '6px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            opacity: currentPage === 1 ? 0.5 : 1
                        }}
                    >
                        Previous
                    </button>
                    
                    <span style={{ padding: '0 12px' }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        style={{
                            padding: '6px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            background: '#fff',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            opacity: currentPage === totalPages ? 0.5 : 1
                        }}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default StudentReport;
